/**
 * « Offrir cette SKILLCARD » (migrations 0326-0327) : sous les offres de la
 * page d'achat, les mêmes lots au PRIX PLEIN (destinataire inconnu). Le
 * paiement crée un code cadeau, retrouvé sur « Mes cadeaux » (`/cadeaux`).
 * Web seulement (App Store 3.1.1), compte requis.
 */

import { useEffect, useRef, useState } from 'react';
import { Gift } from 'lucide-react';
import { useGiftOffers } from '@/lib/skillcards/gifts';
import { skillcardCheckoutErrorMessage, startSkillcardCheckout } from '@/lib/skillcards/purchase';
import { isNativeApp } from '@/lib/platform';
import type { SkillcardOfferKind } from '@jeraisonne/db/cards';
import { SkillcardOfferRow } from './skillcard-offer-row';
import { offerContentLabel } from '@jeraisonne/db/cards';

export function GiftOffersSection({
  cardId,
  signedIn,
  defaultOpen = false,
}: {
  cardId: string;
  signedIn: boolean;
  /** Ouvert d'emblée et amené à l'écran (arrivée par « Offrir » depuis la carte). */
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (defaultOpen) ref.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [defaultOpen]);
  const offers = useGiftOffers(cardId, open && !isNativeApp());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (isNativeApp()) return null;

  async function offer(kind: SkillcardOfferKind, targetId: string) {
    setError(null);
    setBusy(`${kind}:${targetId}`);
    try {
      window.location.href = await startSkillcardCheckout({ kind, targetId, cardId, gift: true });
    } catch (e) {
      setError(skillcardCheckoutErrorMessage((e as Error).message));
      setBusy(null);
    }
  }

  const data = offers.data;
  const rows = data
    ? [
        { q: data.card, title: data.card.label, subtitle: offerContentLabel(data.card) },
        ...data.packs.map((p) => ({
          q: p,
          title: p.label,
          subtitle: `Pack · ${offerContentLabel(p)}`,
        })),
        ...data.programmes.map((p) => ({
          q: p,
          title: p.label,
          subtitle: `Programme complet · ${offerContentLabel(p)}`,
        })),
      ]
    : [];

  return (
    <section
      ref={ref}
      id="offrir"
      className="border-ink-200 ocean:bg-white/5 scroll-mt-4 space-y-3 rounded-2xl border bg-white p-4"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-[2.75rem] w-full items-center gap-3 text-left"
      >
        <span className="bg-accent-500 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white">
          <Gift className="h-5 w-5" aria-hidden />
        </span>
        <span className="min-w-0">
          <span className="text-ink-900 block font-semibold">Offrir à quelqu’un</span>
          <span className="text-ink-600 block text-sm">
            Achète une carte cadeau : un code à partager par message, email ou QR code.
          </span>
        </span>
      </button>
      {open && (
        <div className="space-y-2">
          {!signedIn ? (
            <p className="text-ink-500 text-sm">Connecte-toi pour offrir des SKILLCARDS.</p>
          ) : offers.isLoading ? (
            <div className="bg-ink-100 h-24 animate-pulse rounded-xl" />
          ) : (
            rows.map(({ q, title, subtitle }) => (
              <SkillcardOfferRow
                key={`${q.kind}:${q.target_id}`}
                quote={q}
                title={title}
                subtitle={subtitle}
                buyLabel="Offrir"
                hideDeductionNote
                busy={busy === `${q.kind}:${q.target_id}`}
                disabled={!!busy}
                onBuy={() => void offer(q.kind, q.target_id)}
              />
            ))
          )}
          {error && (
            <p role="alert" className="text-error-600 text-sm">
              {error}
            </p>
          )}
          <p className="text-ink-500 text-xs">
            Prix plein (la personne qui reçoit n’est pas encore connue). Le code est valable sans
            limite de temps ; une fois activé, ses cartes restent actives un an.
          </p>
        </div>
      )}
    </section>
  );
}
